import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// Thin React wrapper around the browser-native Web Speech API
// (SpeechRecognition / webkitSpeechRecognition). On-device or
// cloud-routed depending on browser; zero backend dependency.
//
// Supported: Chrome, Edge, Safari (webkit prefix). Not supported:
// Firefox. The `supported` flag lets the consumer hide the affordance
// when unavailable.
//
// Two behaviours worth knowing:
// 1. **Callbacks are read via ref.** Stale-closure bug: if a consumer's
//    `onFinalText` captures a `value` from props, and SpeechRecognition
//    fires multiple results in the same tick, the second call sees the
//    OLD value (React hasn't re-rendered yet). The hook keeps callbacks
//    fresh via refs so `rec.onresult` always reaches the latest one.
//    Consumers should ALSO use refs for any state they append into —
//    `MicTranscribeButton` does this for `value`.
// 2. **Auto-restart on browser auto-end.** Continuous mode notwithstanding,
//    browsers auto-end the session after a few seconds of silence. We
//    auto-restart unless the user explicitly clicked stop — so "pause
//    and continue speaking" works naturally.

type SpeechResultLike = {
  isFinal: boolean;
  [index: number]: { transcript: string };
};

type SpeechResultListLike = {
  length: number;
  [index: number]: SpeechResultLike;
};

type SpeechRecognitionEventLike = {
  results: SpeechResultListLike;
  resultIndex: number;
};

type SpeechRecognitionErrorLike = {
  error: string;
};

type SpeechRecognitionLike = {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((event: SpeechRecognitionEventLike) => void) | null;
  onerror: ((event: SpeechRecognitionErrorLike) => void) | null;
  onend: (() => void) | null;
};

type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

function getSpeechRecognitionCtor(): SpeechRecognitionCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export function isSpeechRecognitionSupported(): boolean {
  return getSpeechRecognitionCtor() !== null;
}

export type SpeechRecognitionState = 'idle' | 'starting' | 'listening' | 'error';

export type UseSpeechRecognitionOptions = {
  // Called with each newly-finalised transcript chunk. Append-on-receive
  // is the typical use; the consumer decides spacing.
  onFinalText: (text: string) => void;
  // Optional: called as the model refines the in-progress phrase.
  // Useful for rendering a live preview of what's being heard.
  onInterimText?: (text: string) => void;
  // BCP-47 language tag. Defaults to en-US.
  lang?: string;
};

export function useSpeechRecognition({
  onFinalText,
  onInterimText,
  lang = 'en-US',
}: UseSpeechRecognitionOptions) {
  const [state, setState] = useState<SpeechRecognitionState>('idle');
  const [error, setError] = useState<string | null>(null);
  const recRef = useRef<SpeechRecognitionLike | null>(null);
  const userStoppedRef = useRef(true);

  // Stable refs to the callbacks. Updated every render so `rec.onresult`
  // (set once at start() time) always reaches the latest closure. This
  // is half of the stale-closure fix; the consumer also needs a value
  // ref for synchronous append patterns.
  const onFinalTextRef = useRef(onFinalText);
  const onInterimTextRef = useRef(onInterimText);
  useEffect(() => {
    onFinalTextRef.current = onFinalText;
  }, [onFinalText]);
  useEffect(() => {
    onInterimTextRef.current = onInterimText;
  }, [onInterimText]);

  const supported = useMemo(isSpeechRecognitionSupported, []);

  // Stable startSession ref so onend can re-invoke without re-binding.
  const startSessionRef = useRef<(() => void) | null>(null);

  startSessionRef.current = () => {
    const Ctor = getSpeechRecognitionCtor();
    if (!Ctor) {
      setError('Speech recognition not supported in this browser');
      setState('error');
      return;
    }

    recRef.current?.abort();

    const rec = new Ctor();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = lang;

    rec.onresult = (event) => {
      let interim = '';
      let final = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const transcript = result[0].transcript;
        if (result.isFinal) {
          final += transcript;
        } else {
          interim += transcript;
        }
      }
      if (final.length > 0) {
        onFinalTextRef.current?.(final);
      }
      // Always notify interim — including empty — so the consumer can
      // clear its preview when the model finalises a phrase.
      if (onInterimTextRef.current) {
        onInterimTextRef.current(interim);
      }
    };

    rec.onerror = (event) => {
      // 'no-speech' is normal silence in continuous mode; 'aborted'
      // happens when we abort() to start a new session. Neither is a
      // real error — don't surface to the user.
      if (event.error === 'no-speech' || event.error === 'aborted') {
        return;
      }
      setError(event.error);
      setState('error');
    };

    rec.onend = () => {
      // Always clear the interim preview when a session ends so stale
      // text doesn't sit on screen.
      onInterimTextRef.current?.('');

      // Auto-restart if the browser auto-ended (silence timeout) but
      // the user hasn't clicked stop. This makes "pause and continue"
      // feel seamless.
      if (!userStoppedRef.current) {
        setTimeout(() => {
          if (!userStoppedRef.current && startSessionRef.current) {
            startSessionRef.current();
          }
        }, 100);
      } else {
        setState('idle');
      }
    };

    try {
      rec.start();
      recRef.current = rec;
      setState('listening');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start recognition');
      setState('error');
    }
  };

  const start = useCallback(() => {
    userStoppedRef.current = false;
    setError(null);
    setState('starting');
    startSessionRef.current?.();
  }, []);

  const stop = useCallback(() => {
    userStoppedRef.current = true;
    recRef.current?.stop();
    setState('idle');
    onInterimTextRef.current?.('');
  }, []);

  // Cleanup on unmount — abort any active recognition session.
  useEffect(() => {
    return () => {
      userStoppedRef.current = true;
      recRef.current?.abort();
      recRef.current = null;
    };
  }, []);

  return { state, error, start, stop, supported };
}
